import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { getCustomFields, createCustomField, updateCustomField, deleteCustomField } from '../../services/api';
import { ListChecks, Plus, PencilLine, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const cfTypeLabels = { text: 'Texte', number: 'Nombre', date: 'Date', select: 'Liste', checkbox: 'Case a cocher', textarea: 'Zone de texte' };

export function CustomFieldsSettings() {
  const [cfDefs, setCfDefs] = useState([]);
  const [cfDialog, setCfDialog] = useState(false);
  const [cfEditId, setCfEditId] = useState(null);
  const [cfForm, setCfForm] = useState({ label: '', field_type: 'text', options: '', required: false });

  useEffect(() => { fetchCustomFieldDefs(); }, []);

  const fetchCustomFieldDefs = async () => { try { const res = await getCustomFields('contacts'); setCfDefs(res.data.items || []); } catch {} };

  const handleCfSubmit = async () => {
    try {
      const payload = { label: cfForm.label, field_type: cfForm.field_type, required: cfForm.required, applies_to: 'contacts', options: cfForm.field_type === 'select' ? cfForm.options.split(',').map(o => o.trim()).filter(Boolean) : [] };
      if (cfEditId) { await updateCustomField(cfEditId, payload); toast.success('Champ mis a jour'); }
      else { await createCustomField(payload); toast.success('Champ cree'); }
      setCfDialog(false); setCfEditId(null); setCfForm({ label: '', field_type: 'text', options: '', required: false }); fetchCustomFieldDefs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCfEdit = (field) => {
    setCfEditId(field.id);
    setCfForm({ label: field.label, field_type: field.field_type, options: (field.options || []).join(', '), required: field.required || false });
    setCfDialog(true);
  };

  const handleCfDelete = async (id) => {
    if (!window.confirm('Supprimer ce champ personnalise ?')) return;
    try { await deleteCustomField(id); toast.success('Champ supprime'); fetchCustomFieldDefs(); } catch { toast.error('Erreur'); }
  };

  return (
    <>
      <Card className="border-[#E8E6E1]" data-testid="custom-fields-config">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between" style={{ fontFamily: 'Manrope' }}>
            <div className="flex items-center gap-2"><ListChecks size={18} className="text-[#2D5A4C]" /> Champs personnalises (Contacts)</div>
            <Button size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white" onClick={() => { setCfEditId(null); setCfForm({ label: '', field_type: 'text', options: '', required: false }); setCfDialog(true); }} data-testid="add-custom-field-btn"><Plus size={14} className="mr-1" /> Ajouter</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cfDefs.length === 0 ? (
            <p className="text-sm text-[#5E6D66] text-center py-4">Aucun champ personnalise. Cliquez sur "Ajouter" pour en creer un.</p>
          ) : (
            <div className="space-y-2">
              {cfDefs.map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 border border-[#E8E6E1] rounded-lg hover:bg-[#F9F8F5] transition-colors" data-testid={`cf-row-${f.name}`}>
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-[#1A2421]">{f.label}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px]">{cfTypeLabels[f.field_type] || f.field_type}</Badge>
                        {f.required && <Badge className="text-[10px] bg-amber-100 text-amber-800">Requis</Badge>}
                        {f.field_type === 'select' && f.options?.length > 0 && <span className="text-[10px] text-[#5E6D66]">{f.options.length} options</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleCfEdit(f)} data-testid={`cf-edit-${f.name}`}><PencilLine size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleCfDelete(f.id)} className="text-red-500 hover:text-red-700" data-testid={`cf-delete-${f.name}`}><Trash size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={cfDialog} onOpenChange={setCfDialog}>
        <DialogContent className="max-w-md" data-testid="custom-field-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{cfEditId ? 'Modifier le champ' : 'Nouveau champ personnalise'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Libelle *</Label><Input value={cfForm.label} onChange={(e) => setCfForm(prev => ({ ...prev, label: e.target.value }))} placeholder="Ex: Numero SIRET, Date d'adhesion..." data-testid="cf-form-label" /></div>
            <div className="space-y-2"><Label>Type de champ</Label><Select value={cfForm.field_type} onValueChange={(v) => setCfForm(prev => ({ ...prev, field_type: v }))}><SelectTrigger data-testid="cf-form-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="text">Texte</SelectItem><SelectItem value="number">Nombre</SelectItem><SelectItem value="date">Date</SelectItem><SelectItem value="select">Liste deroulante</SelectItem><SelectItem value="checkbox">Case a cocher</SelectItem><SelectItem value="textarea">Zone de texte</SelectItem></SelectContent></Select></div>
            {cfForm.field_type === 'select' && (<div className="space-y-2"><Label>Options (separees par virgule)</Label><Input value={cfForm.options} onChange={(e) => setCfForm(prev => ({ ...prev, options: e.target.value }))} placeholder="Option 1, Option 2, Option 3" data-testid="cf-form-options" /></div>)}
            <div className="flex items-center gap-2"><Switch checked={cfForm.required} onCheckedChange={(v) => setCfForm(prev => ({ ...prev, required: v }))} data-testid="cf-form-required" /><Label>Champ requis</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfDialog(false)}>Annuler</Button>
            <Button onClick={handleCfSubmit} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="cf-form-submit">{cfEditId ? 'Mettre a jour' : 'Creer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
